import { useState } from "react";
import {
  isSameDay,
  startOfMonth,
  endOfMonth,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isToday,
  isSameMonth,
  format,
} from "date-fns";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Plane, LogOut } from "lucide-react";

interface BookingsCalendarProps {
  rows: any[];
}

const statusColors: Record<string, string> = {
  pending: "bg-yellow-500/15 text-yellow-700 border-yellow-200",
  confirmed: "bg-blue-500/15 text-blue-700 border-blue-200",
};

const statusLabels: Record<string, string> = {
  pending: "Pending",
  confirmed: "Confirmed",
};

const paymentMethodLabels: Record<string, string> = {
  cash: "Cash",
  paypal: "PayPal",
  stripe: "Card",
};

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function getCalendarDays(month: Date) {
  const start = startOfWeek(startOfMonth(month));
  const end = endOfWeek(endOfMonth(month));
  return eachDayOfInterval({ start, end });
}

function getBookingsForDay(day: Date, bookings: any[]) {
  const arrivals = bookings.filter((b) => isSameDay(new Date(b.pickup_date), day));
  const departures = bookings.filter((b) => b.return_date && isSameDay(new Date(b.return_date), day));
  return { arrivals, departures };
}

export function BookingsCalendar({ rows }: BookingsCalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);

  const filtered = rows.filter(
    (b) => b.status === "confirmed" || (b.status === "pending" && b.payment_method === "cash")
  );

  const days = getCalendarDays(currentMonth);
  const selectedDayData = selectedDay ? getBookingsForDay(selectedDay, filtered) : null;

  return (
    <div>
      {/* Month navigation */}
      <div className="flex items-center justify-between mb-4">
        <Button variant="outline" size="icon" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <h3 className="text-lg font-semibold">{format(currentMonth, "MMMM yyyy")}</h3>
        <Button variant="outline" size="icon" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Calendar grid */}
      <Card>
        <CardContent className="p-2 sm:p-4">
          <div className="grid grid-cols-7 gap-1">
            {/* Weekday headers */}
            {WEEKDAYS.map((d) => (
              <div key={d} className="text-center text-xs font-semibold text-muted-foreground py-2">
                {d}
              </div>
            ))}

            {/* Day cells */}
            {days.map((day) => {
              const inMonth = isSameMonth(day, currentMonth);
              const today = isToday(day);
              const { arrivals, departures } = getBookingsForDay(day, filtered);
              const hasArrivals = arrivals.length > 0;
              const hasDepartures = departures.length > 0;
              const hasEvents = hasArrivals || hasDepartures;

              let cellBg = "";
              if (hasArrivals && hasDepartures) cellBg = "bg-purple-100 dark:bg-purple-900/30";
              else if (hasArrivals) cellBg = "bg-purple-50 dark:bg-purple-900/20";
              else if (hasDepartures) cellBg = "bg-green-50 dark:bg-green-900/20";

              return (
                <button
                  key={day.toISOString()}
                  onClick={() => hasEvents && setSelectedDay(day)}
                  disabled={!hasEvents}
                  className={`
                    relative min-h-[70px] sm:min-h-[85px] rounded-md border p-1 text-left transition-all
                    ${inMonth ? "" : "opacity-30"}
                    ${today ? "ring-2 ring-primary" : ""}
                    ${cellBg}
                    ${hasEvents ? "cursor-pointer hover:ring-2 hover:ring-primary/50" : "cursor-default"}
                  `}
                >
                  <span className={`text-xs font-medium ${today ? "text-primary font-bold" : "text-foreground"}`}>
                    {format(day, "d")}
                  </span>
                  {hasArrivals && (
                    <div className="flex items-center gap-0.5 mt-0.5">
                      <Plane className="h-3 w-3 text-purple-600 dark:text-purple-400" />
                      <span className="text-[10px] font-semibold text-purple-700 dark:text-purple-300">
                        {arrivals.length}
                      </span>
                    </div>
                  )}
                  {hasDepartures && (
                    <div className="flex items-center gap-0.5 mt-0.5">
                      <LogOut className="h-3 w-3 text-green-600 dark:text-green-400" />
                      <span className="text-[10px] font-semibold text-green-700 dark:text-green-300">
                        {departures.length}
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <div className="h-3 w-3 rounded-sm bg-purple-400" />
          <span>Arrivals (Pickup)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-3 w-3 rounded-sm bg-green-400" />
          <span>Departures (Return)</span>
        </div>
      </div>

      {/* Day detail dialog */}
      <Dialog open={!!selectedDay} onOpenChange={(open) => !open && setSelectedDay(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedDay && format(selectedDay, "EEEE, MMMM d, yyyy")}</DialogTitle>
          </DialogHeader>

          {selectedDayData && (
            <div className="space-y-4">
              {selectedDayData.arrivals.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-purple-700 dark:text-purple-300 mb-2 flex items-center gap-1">
                    <Plane className="h-4 w-4" /> Arrivals ({selectedDayData.arrivals.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedDayData.arrivals.map((b: any) => (
                      <BookingCard key={b.id} booking={b} />
                    ))}
                  </div>
                </div>
              )}
              {selectedDayData.departures.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-green-700 dark:text-green-300 mb-2 flex items-center gap-1">
                    <LogOut className="h-4 w-4" /> Departures ({selectedDayData.departures.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedDayData.departures.map((b: any) => (
                      <BookingCard key={b.id} booking={b} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Close</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BookingCard({ booking }: { booking: any }) {
  return (
    <div className="rounded-lg border p-3 space-y-1 text-sm">
      <div className="flex items-center justify-between">
        <span className="font-mono text-xs font-bold text-primary">{booking.booking_code || "—"}</span>
        <Badge variant="outline" className={statusColors[booking.status] || ""}>
          {statusLabels[booking.status] || booking.status}
        </Badge>
      </div>
      <div className="font-medium">{booking.customer_name}</div>
      <div className="text-xs text-muted-foreground">{booking.customer_phone}</div>
      <div className="text-xs">
        <span>{booking.pickup_location}</span>
        <span className="text-muted-foreground"> → </span>
        <span>{booking.dropoff_location}</span>
      </div>
      <div className="flex items-center justify-between pt-1">
        <span className="text-xs text-muted-foreground">
          {(booking.vehicles as any)?.name || "—"} · {paymentMethodLabels[booking.payment_method] || booking.payment_method}
        </span>
        <span className="font-semibold text-green-700">${booking.total_price}</span>
      </div>
      <div className="text-xs text-muted-foreground">
        Pickup: {format(new Date(booking.pickup_date), "PPP 'at' p")}
        {booking.return_date && <> · Return: {format(new Date(booking.return_date), "PPP 'at' p")}</>}
      </div>
    </div>
  );
}
